import EditTrialDialog from "@/components/EditTrialDialog";
import NewTrialForm from "@/components/NewTrialForm";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigationGuard } from "@/hooks/useNavigationGuard";
import { useOrgPaths } from "@/hooks/useOrgPaths";
import { useRetainedValue } from "@/hooks/useRetainedValue";
import { useToast } from "@/hooks/use-toast";
import { useTrials } from "@/hooks/useFirestore";
import { useCreateTrial, useDeleteTrial } from "@/services/dataService";
import { Trial } from "@/types/trial";
import { EmptyState } from "@/components/ui/empty-state";
import { Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import TransitionOverlay from "@/components/TransitionOverlay";
import { ensureMinDuration } from "@/utils/delay";
import TrialCard from "../components/TrialCard";

const MIN_CREATION_TRANSITION_MS = 1500;

const Index = () => {
  const { trials, loading, error } = useTrials();
  const { userRole } = useAuth();
  const createTrial = useCreateTrial();
  const deleteTrial = useDeleteTrial();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isAddTrialDirty, setIsAddTrialDirty] = useState(false);
  // Warn before discarding an in-progress new trial (Escape, click-outside, X, Cancel).
  const addTrialGuard = useUnsavedChangesGuard({
    isDirty: isAddTrialDirty,
    onOpenChange: setIsAddDialogOpen,
  });
  const [trialToDelete, setTrialToDelete] = useState<string | null>(null);
  const [trialToEdit, setTrialToEdit] = useState<Trial | null>(null);
  const [trialEditSession, setTrialEditSession] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreatingTrial, setIsCreatingTrial] = useState(false);
  const [creationOverlay, setCreationOverlay] = useState<{
    open: boolean;
    title: string;
    subtitle?: string;
    startedAtMs: number;
  } | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const paths = useOrgPaths();
  const beginNavigationGuard = useNavigationGuard();

  // Role-based permissions
  // Collectors, editors, and admins can create/edit/delete (collector restrictions enforced server-side)
  const canEdit = userRole && ["collector", "editor", "admin"].includes(userRole);
  const canDelete = userRole && ["collector", "editor", "admin"].includes(userRole);

  const filteredTrials = useMemo(() => {
    if (!searchTerm.trim()) return trials;

    const searchLower = searchTerm.toLowerCase();
    return trials.filter((trial) => {
      const nameMatch = trial.name.toLowerCase().includes(searchLower);
      const locationMatch = trial.locationName?.toLowerCase().includes(searchLower);
      const cropsMatch = trial.crops?.some((crop) => crop.toLowerCase().includes(searchLower));
      const descriptionMatch = trial.description?.toLowerCase().includes(searchLower);

      // TODO(FROM LOVABLE): Add user search functionality once user data is resolved from createdBy reference
      // This would require extending the useTrials hook to resolve user references to get firstName, lastName, email
      // Example: const userMatch = trial.createdByUser &&
      //   `${trial.createdByUser.firstName} ${trial.createdByUser.lastName} ${trial.createdByUser.email}`.toLowerCase().includes(searchLower);

      return nameMatch || locationMatch || cropsMatch || descriptionMatch;
    });
  }, [trials, searchTerm]);
  const renderedTrialToEdit = useRetainedValue(trialToEdit);
  const renderedTrialToDelete = useRetainedValue(trialToDelete);

  const confirmDelete = async (trialId: string) => {
    // Find the trial name for the overlay message
    const trial = trials.find((t) => t.id === trialId);
    const trialName = trial?.name || "trial";

    // Don't manually close the dialog - let Radix's AlertDialogAction
    // handle the close via onOpenChange to ensure proper body pointer-events cleanup.
    setCreationOverlay({
      open: true,
      title: "Deleting trial...",
      subtitle: `Removing "${trialName}"...`,
      startedAtMs: Date.now(),
    });

    try {
      await deleteTrial(trialId);
      toast({
        title: "Trial deleted",
        description: "The trial and all associated data have been removed.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete the trial. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCreationOverlay(null);
    }
  };

  const handleAddTrial = async (newTrial: Partial<Trial>) => {
    setIsCreatingTrial(true);
    const startedAtMs = Date.now();
    setCreationOverlay({
      open: true,
      title: "Creating trial...",
      subtitle: "Taking you to your new trial...",
      startedAtMs,
    });

    const stillHere = beginNavigationGuard();
    try {
      const createdTrial = await createTrial(newTrial);

      if (createdTrial) {
        await ensureMinDuration(startedAtMs, MIN_CREATION_TRANSITION_MS);
        setCreationOverlay(null);
        setIsAddDialogOpen(false);
        toast({
          title: "Trial created",
          description: "Your new trial has been created.",
        });
        // Navigate to the newly created trial, unless the user already went elsewhere
        if (stillHere()) {
          navigate(paths.trial(createdTrial.id));
        }
      } else {
        setCreationOverlay(null);
        toast({
          title: "Error",
          description: "Failed to create the trial. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error creating trial:", error);
      setCreationOverlay(null);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create the trial. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreatingTrial(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center items-center h-64">
          <p>Loading trials...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center items-center h-64">
          <p className="text-destructive">Error loading trials: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <TransitionOverlay
        open={creationOverlay?.open ?? false}
        title={creationOverlay?.title ?? ""}
        subtitle={creationOverlay?.subtitle}
      />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-agri-green-dark">My Trials</h1>
        {canEdit && (
          <Button
            variant="default"
            onClick={() => setIsAddDialogOpen(true)}
            size="sm">
            <Plus className="mr-1 h-4 w-4" /> Add Trial
          </Button>
        )}
      </div>

      {/* Search Box */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search trials by name, location, crop, description, or creator..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {trials.length === 0 ? (
        <EmptyState
          title="No trials yet"
          description="Create a trial to start managing your agricultural experiments"
          buttonText="Add First Trial"
          onAction={() => setIsAddDialogOpen(true)}
          canEdit={canEdit}
        />
      ) : filteredTrials.length === 0 ? (
        <EmptyState
          title="No trials match your search"
          description="Try adjusting your search terms"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 items-stretch">
          {filteredTrials.map((trial) => (
            <div
              key={trial.id}
              className="relative">
              <TrialCard
                trial={trial}
                onDeleteTrial={() => setTrialToDelete(trial.id)}
                onEditTrial={() => {
                  setTrialToEdit(trial);
                  setTrialEditSession((current) => current + 1);
                }}
                canEdit={canEdit}
                canDelete={canDelete}
              />
            </div>
          ))}
        </div>
      )}

      {/* Add Trial Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onOpenChange={addTrialGuard.guardedOnOpenChange}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader className="px-1">
            <DialogTitle>Create New Trial</DialogTitle>
            <DialogDescription>Fill in the details to create a new agricultural trial.</DialogDescription>
          </DialogHeader>
          <NewTrialForm
            onSubmit={handleAddTrial}
            onCancel={addTrialGuard.requestClose}
            isSubmitting={isCreatingTrial}
            onDirtyChange={setIsAddTrialDirty}
          />
        </DialogContent>
      </Dialog>

      {addTrialGuard.confirmation}

      {/* Edit Trial Dialog */}
      {renderedTrialToEdit && (
        <EditTrialDialog
          key={`${renderedTrialToEdit.id}-${trialEditSession}`}
          open={trialToEdit !== null}
          onOpenChange={(isOpen) => !isOpen && setTrialToEdit(null)}
          trial={renderedTrialToEdit}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={trialToDelete !== null}
        onOpenChange={(isOpen) => !isOpen && setTrialToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this trial and all associated tasks and collected data. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (renderedTrialToDelete) {
                  void confirmDelete(renderedTrialToDelete);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete Trial
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Index;
